import * as z from "zod";

// Layer 1: question type schemas. A closed, code-defined discriminated union
// of step types. Each variant carries the structural fields its instances
// need (e.g. options[] for selects). Content (label, description, placeholder)
// is on every question type's instances. Concrete authored question instances
// live in hsa-question-pool.ts.
//
// Adding a new question type is an engineering change: define the variant
// here, extend HsaAnswer in hsa-answer.ts, extend the runtime answer
// validator and the condition operator union if applicable.
//
// Convention enforcement (e.g. SINGLE_SELECT_OTHER_QUESTION must include an
// option with value "other") happens in hsa-flow-validate.ts and a pool
// integrity unit test, NOT in z.refine() — keeps the schemas as plain
// ZodObject so discriminatedUnion stays clean.

export enum HsaQuestionType {
  AGE_INPUT_QUESTION = "AGE_INPUT_QUESTION",
  EMAIL_INPUT_QUESTION = "EMAIL_INPUT_QUESTION",
  TEXT_INPUT_QUESTION = "TEXT_INPUT_QUESTION",
  SINGLE_SELECT_RADIO_QUESTION = "SINGLE_SELECT_RADIO_QUESTION",
  SINGLE_SELECT_DROPDOWN_QUESTION = "SINGLE_SELECT_DROPDOWN_QUESTION",
  SINGLE_SELECT_OTHER_QUESTION = "SINGLE_SELECT_OTHER_QUESTION",
  MULTI_SELECT_QUESTION = "MULTI_SELECT_QUESTION",
  MULTI_SELECT_NONE_QUESTION = "MULTI_SELECT_NONE_QUESTION",
}

// Convention markers for option values that participate in special UI
// behaviors. Validator enforces presence of these on the relevant question
// types.
export const SINGLE_SELECT_OTHER_OPTION_VALUE = "other";
export const MULTI_SELECT_NONE_OPTION_VALUE = "none";

const hsaSelectOption_ = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  // Optional emoji or icon identifier the UI may render alongside the option.
  icon: z.string().optional(),
});
export const hsaSelectOption: z.ZodType<HsaSelectOption> = hsaSelectOption_;
export interface HsaSelectOption extends z.infer<typeof hsaSelectOption_> {}

const hsaQuestionContentBase_ = z.object({
  label: z.string().min(1),
  description: z.string().optional(),
  placeholder: z.string().optional(),
  required: z.boolean(),
});

// AGE — integer 0–200, "years" suffix. Regex + validation message baked in
// at the answer-validator layer; not configurable per pool entry.
const hsaAgeInputQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.AGE_INPUT_QUESTION),
});
export const hsaAgeInputQuestion: z.ZodType<HsaAgeInputQuestion> =
  hsaAgeInputQuestion_;
export interface HsaAgeInputQuestion
  extends z.infer<typeof hsaAgeInputQuestion_> {}

// EMAIL — RFC-5322-ish regex baked in at the answer-validator layer; not
// configurable per pool entry.
const hsaEmailInputQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.EMAIL_INPUT_QUESTION),
});
export const hsaEmailInputQuestion: z.ZodType<HsaEmailInputQuestion> =
  hsaEmailInputQuestion_;
export interface HsaEmailInputQuestion
  extends z.infer<typeof hsaEmailInputQuestion_> {}

// TEXT — open text with optional regex. Both the regex and its error message
// are configurable per pool entry (e.g. first+last name uses ".+\s.+";
// other text questions might not validate at all).
const hsaTextInputQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.TEXT_INPUT_QUESTION),
  validationRegex: z.string().optional(),
  validationMessage: z.string().optional(),
});
export const hsaTextInputQuestion: z.ZodType<HsaTextInputQuestion> =
  hsaTextInputQuestion_;
export interface HsaTextInputQuestion
  extends z.infer<typeof hsaTextInputQuestion_> {}

const hsaSingleSelectRadioQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION),
  options: z.array(hsaSelectOption_).min(2),
});
export const hsaSingleSelectRadioQuestion: z.ZodType<HsaSingleSelectRadioQuestion> =
  hsaSingleSelectRadioQuestion_;
export interface HsaSingleSelectRadioQuestion
  extends z.infer<typeof hsaSingleSelectRadioQuestion_> {}

const hsaSingleSelectDropdownQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION),
  options: z.array(hsaSelectOption_).min(2),
});
export const hsaSingleSelectDropdownQuestion: z.ZodType<HsaSingleSelectDropdownQuestion> =
  hsaSingleSelectDropdownQuestion_;
export interface HsaSingleSelectDropdownQuestion
  extends z.infer<typeof hsaSingleSelectDropdownQuestion_> {}

// SINGLE_SELECT_OTHER — radios plus an "other" option that opens an inline
// free-text field. Validator enforces presence of an option whose value
// equals SINGLE_SELECT_OTHER_OPTION_VALUE.
const hsaSingleSelectOtherQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION),
  options: z.array(hsaSelectOption_).min(2),
});
export const hsaSingleSelectOtherQuestion: z.ZodType<HsaSingleSelectOtherQuestion> =
  hsaSingleSelectOtherQuestion_;
export interface HsaSingleSelectOtherQuestion
  extends z.infer<typeof hsaSingleSelectOtherQuestion_> {}

const hsaMultiSelectQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.MULTI_SELECT_QUESTION),
  options: z.array(hsaSelectOption_).min(1),
  minSelections: z.number().int().nonnegative().optional(),
  maxSelections: z.number().int().positive().optional(),
});
export const hsaMultiSelectQuestion: z.ZodType<HsaMultiSelectQuestion> =
  hsaMultiSelectQuestion_;
export interface HsaMultiSelectQuestion
  extends z.infer<typeof hsaMultiSelectQuestion_> {}

// MULTI_SELECT_NONE — checkboxes with one exclusive "none of the above"
// option. Validator enforces presence of an option whose value equals
// MULTI_SELECT_NONE_OPTION_VALUE. Selecting "none" must clear all other
// selections; selecting any other option must clear "none". This logic
// lives in the answer-validator and the customer-portal UI.
const hsaMultiSelectNoneQuestion_ = hsaQuestionContentBase_.extend({
  type: z.literal(HsaQuestionType.MULTI_SELECT_NONE_QUESTION),
  options: z.array(hsaSelectOption_).min(1),
});
export const hsaMultiSelectNoneQuestion: z.ZodType<HsaMultiSelectNoneQuestion> =
  hsaMultiSelectNoneQuestion_;
export interface HsaMultiSelectNoneQuestion
  extends z.infer<typeof hsaMultiSelectNoneQuestion_> {}

const hsaQuestion_ = z.discriminatedUnion("type", [
  hsaAgeInputQuestion_,
  hsaEmailInputQuestion_,
  hsaTextInputQuestion_,
  hsaSingleSelectRadioQuestion_,
  hsaSingleSelectDropdownQuestion_,
  hsaSingleSelectOtherQuestion_,
  hsaMultiSelectQuestion_,
  hsaMultiSelectNoneQuestion_,
]);
export const hsaQuestion: z.ZodType<HsaQuestion> = hsaQuestion_;
export type HsaQuestion = z.infer<typeof hsaQuestion_>;
